/**
 * Shared caller-identity classifier.
 *
 * No server-only or browser-only dependencies -- importable from both
 * server code (via @shared/caller-identity) and client code (same alias).
 *
 * Classification rules (in priority order):
 *  1. null / empty / known privacy token  -> 'anonymous'
 *  2. RFC 3325 anonymous SIP URI          -> 'anonymous'
 *  3. 2-6 digit short code               -> 'internal_extension'
 *  4. Tenant SIP endpoint username (pf.../cf.../pfx.../cfx... prefix) -> 'sip_endpoint'
 *  5. 7-15 digits after stripping formatting -> 'external_phone'
 *  6. Anything else (arbitrary text)      -> 'unknown'
 *
 * Only 'external_phone' values may be used as a Plivo caller-ID or stored as
 * a contact phone number.  'anonymous' and 'unknown' must both be rejected at
 * every contact-creation, SMS, CSAT, and callback gate.
 */

export type CallerKind =
  | 'anonymous'          // privacy-withheld / cannot be used as caller-ID
  | 'external_phone'     // valid E.164 or phone-like: 7-15 digits
  | 'internal_extension' // short 2-6 digit internal extension
  | 'sip_endpoint'       // tenant-registered SIP endpoint username
  | 'unknown';           // non-empty value that matches no known category

/**
 * Raw tokens emitted by Plivo (and carriers generally) when the caller
 * withholds their number.  All lowercase -- matched after normalisation.
 */
export const ANONYMOUS_CALLER_TOKENS = new Set([
  'anonymous',
  'unknown',
  'private',
  'restricted',
  'withheld',
  'unavailable',
  'blocked',
  '',
]);

/**
 * Normalise a raw caller-ID string:
 *  - Safely URL-decode (malformed sequences fall back to the raw value)
 *  - Trim whitespace
 *  - Strip angle-bracket wrapping  <sip:user@host> -> sip:user@host
 *  - Lowercase
 *  - For SIP URIs: extract the user part; detect anonymous hosts
 *
 * Returns '__anonymous_sip__' when the SIP host signals a privacy address
 * (RFC 3325: host is anonymous.invalid or a sub-domain thereof).
 */
function normalizeCallerRaw(raw: string): string {
  if (!raw) return '';
  let s: string;
  try {
    s = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding -- use the raw value verbatim.
    s = raw;
  }
  s = s.trim().toLowerCase().replace(/^<|>$/g, '');

  if (s.startsWith('sip:') || s.startsWith('sips:')) {
    const withoutScheme = s.replace(/^sips?:/, '');
    const atIdx = withoutScheme.indexOf('@');
    const userPart = atIdx >= 0 ? withoutScheme.slice(0, atIdx) : withoutScheme;
    const hostPart = atIdx >= 0 ? withoutScheme.slice(atIdx + 1) : '';
    // RFC 3325 s.4: anonymous.invalid (or any sub-domain) means the caller
    // has withheld their identity.
    if (hostPart === 'anonymous.invalid' || hostPart.endsWith('.anonymous.invalid')) {
      return '__anonymous_sip__';
    }
    return userPart;
  }

  return s;
}

/**
 * Returns true when the normalised value looks like a real phone number:
 * 7-15 digits after stripping permitted formatting characters
 * (plus sign, spaces, dashes, dots, parentheses).
 *
 * Arbitrary text -- even if it contains some digits -- does NOT pass this
 * check unless the stripped result is entirely digits in the 7-15 range.
 */
function isPhoneLike(s: string): boolean {
  const stripped = s.replace(/[\s\-\.\(\)\+]/g, '');
  return /^\d{7,15}$/.test(stripped);
}

/**
 * Classify the identity kind of a raw caller-ID value.
 *
 * @param raw                The raw From / caller-ID string.  May be URL-encoded,
 *                           a SIP URI, a plain E.164 number, an anonymous token, etc.
 * @param knownSipUsernames  Optional set of active tenant-registered SIP usernames.
 *                           When provided, a username in this set is classified as
 *                           'sip_endpoint' before the prefix-pattern fallback.
 */
export function classifyCallerIdentity(
  raw: string | null | undefined,
  knownSipUsernames?: Set<string>,
): { kind: CallerKind; normalized: string } {
  if (!raw) return { kind: 'anonymous', normalized: '' };

  const n = normalizeCallerRaw(raw);

  // RFC 3325 anonymous SIP URI
  if (n === '__anonymous_sip__') return { kind: 'anonymous', normalized: n };

  // Known privacy / anonymous tokens (already lowercased)
  if (ANONYMOUS_CALLER_TOKENS.has(n)) return { kind: 'anonymous', normalized: n };

  // Dialler "Ext " / "Ext-" prefix pattern (e.g. "Ext 1005", "ext-200")
  if (/^ext[\s-]/i.test(n)) return { kind: 'internal_extension', normalized: n };

  // 2-6 digit internal extension numbers
  if (/^\d{2,6}$/.test(n)) return { kind: 'internal_extension', normalized: n };

  // Tenant-registered SIP endpoint username (explicit set takes priority)
  if (knownSipUsernames && knownSipUsernames.has(n)) {
    return { kind: 'sip_endpoint', normalized: n };
  }

  // pf.../cf.../pfx.../cfx... -- Plivo endpoint provisioner username patterns.
  // Note: the glob characters are not literal here; we match the prefix only.
  if (/^(pf|cf|pfx|cfx)[a-z0-9]/i.test(n)) {
    return { kind: 'sip_endpoint', normalized: n };
  }

  // Valid phone number: 7-15 digits after stripping permitted formatting
  if (isPhoneLike(n)) {
    return { kind: 'external_phone', normalized: n };
  }

  // Arbitrary text that matches no known category -- must never become a
  // telephone number or be used as a Plivo caller-ID.
  return { kind: 'unknown', normalized: n };
}

/**
 * Returns true when the caller-ID represents an anonymous / private caller
 * whose number cannot be shown to agents or used as a Plivo caller-ID.
 */
export function isAnonymous(raw: string | null | undefined): boolean {
  return classifyCallerIdentity(raw).kind === 'anonymous';
}

/**
 * Returns true when the value is either anonymous OR arbitrary unclassifiable
 * text.  Use this gate for contact-creation, SMS send, CSAT trigger, and
 * callback initiation -- anywhere a caller number must be a real phone number.
 */
export function isAnonymousOrInvalid(raw: string | null | undefined): boolean {
  const { kind } = classifyCallerIdentity(raw);
  return kind === 'anonymous' || kind === 'unknown';
}

/**
 * Frontend / UI convenience alias for isAnonymous().
 * Named to make call-site intent clear in component code.
 */
export function isAnonymousCallerToken(raw: string | null | undefined): boolean {
  return isAnonymous(raw);
}
